import { Injectable, OnDestroy } from "@angular/core";
import { IAccountLookupService } from "../IAccountLookupService";
import { ClientAccountVisibilityType } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityType";
import { ClientAccountVisibilityService } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityService";
import { ClientLookupService } from "./ClientLookupService";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { AutoCompleteLabelValue } from "../../../model/core/AutoCompleteLabelValue";
import { NullProtoObject } from "../../../../helper-services/null-proto-object";
import { Subscription } from "rxjs";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { AutoCompleteLabelValueLookupService } from "./AutoCompleteLabelValueLookupService";

@Injectable()
export class AccountLookupService
  extends AutoCompleteLabelValueLookupService<string, CubiqTS.AccountImpl>
  implements IAccountLookupService, OnDestroy
{
  private myAccountsByClientMap: NullProtoObject<CubiqTS.AccountImpl[]> = Object.create(null);
  private privateAccountsByClientMap: NullProtoObject<CubiqTS.AccountImpl[]> = Object.create(null);

  // user may not enter orders for these searchable clients
  protected visibleItemMap: Map<any, any> = new Map();
  //
  protected privatizationItemMap: Map<any, any> = new Map();
  private visibilityServiceSubscription: Subscription;

  constructor(
    protected clientLookup: ClientLookupService,
    protected visibilityService: ClientAccountVisibilityService,
    protected clientAccountVisibilityService: ClientAccountVisibilityService,
  ) {
    super(CubiqTS.AccountImpl);
  }

  itemAsIdLabelValue(item: CubiqTS.AccountImpl): AutoCompleteLabelValue {
    return {
      label: item.id,
      value: item.id,
      __bfx_visibility_type: ClientAccountVisibilityType.getVisibilityType(item),
    };
  }

  itemAsNameLabelValue(item: CubiqTS.AccountImpl): AutoCompleteLabelValue {
    return {
      label: item.name,
      value: item.id,
      __bfx_visibility_type: ClientAccountVisibilityType.getVisibilityType(item),
    };
  }

  getItem(id: string): CubiqTS.AccountImpl {
    let account = super.getItem(id);
    if (!account) {
      account = this.visibleItemMap.get(id);
    }
    if (!account) {
      account = this.privatizationItemMap.get(id);
    }
    return account;
  }

  getItemKey(item: CubiqTS.AccountImpl): string {
    return item.id;
  }

  deleteItem(item): void {
    let key: string = this.getItemKey(item);

    if (key != null) {
      this.myItemMap.delete(key);
    }

    if (item.associatedClients != null) {
      item.associatedClients.forEach((clientId: string) => {
        if (Array.isArray(this.myAccountsByClientMap[clientId])) {
          this.myAccountsByClientMap[clientId] = this.myAccountsByClientMap[clientId].filter(
            (account) => {
              return item.id != account.id;
            },
          );
        }
        if (Array.isArray(this.privateAccountsByClientMap[clientId])) {
          this.privateAccountsByClientMap[clientId] = this.privateAccountsByClientMap[
            clientId
          ].filter((account) => {
            return item.id != account.id;
          });
        }
      });
    }
  }

  mapItem(item: CubiqTS.AccountImpl): void | Promise<void> {
    const notifyUpdatedFn = this.visibilityService.updateTradableAccounts(item);
    return this.visibilityService.getVisibilityType(item).then((type: ClientAccountVisibilityType) => {
      ClientAccountVisibilityType.setVisibilityType(item, type);

      switch (type) {
        case ClientAccountVisibilityType.Full:
        case ClientAccountVisibilityType.Visible:
          super.mapItem(item);
          this.mapVisibleItem(item, item.id);

          if (item.associatedClients != null) {
            item.associatedClients.forEach((clientId: string) => {
              if (!this.myAccountsByClientMap[clientId]) {
                this.myAccountsByClientMap[clientId] = [];
              }
              this.myAccountsByClientMap[clientId].push(item);
            });
          }

          this.mapItemInTreeSet(this.itemAsIdLabelValue(item), this.idLabelValueSet);
          this.mapItemInTreeSetByValue(this.itemAsNameLabelValue(item), this.nameLabelValueSet);

          break;
        case ClientAccountVisibilityType.Privatized:
          this.deleteItem(item);
          this.mapPrivatizationItem(item, item.id);

          if (item.associatedClients != null) {
            item.associatedClients.forEach((clientId: string) => {
              if (!this.privateAccountsByClientMap[clientId]) {
                this.privateAccountsByClientMap[clientId] = [];
              }
              this.privateAccountsByClientMap[clientId].push(item);
            });
          }
          break;
        case ClientAccountVisibilityType.None:
        default:
          // FIXME: Remove in production
          logger.out.warn(
            `${this.constructor.name}.mapItem() dropping account ${item.id} with ClientAccountVisibilityType.None`,
          );
          break;
      }
      notifyUpdatedFn();
    });
  }

  mapVisibleItem(item: CubiqTS.AccountImpl, id?: string): void {
    this.visibleItemMap.set(id ? id : item.clientId, item);
  }

  mapPrivatizationItem(item: CubiqTS.AccountImpl, id?: string): void {
    this.privatizationItemMap.set(id ? id : item.clientId, item);
  }

  ngOnDestroy(): void {
    if (this.visibilityServiceSubscription != null) {
      this.visibilityServiceSubscription.unsubscribe();
      this.visibilityServiceSubscription = null;
    }
  }

  primeCompleted(): void {
    super.primeCompleted();

    this.visibilityServiceSubscription = this.clientAccountVisibilityService.client$.subscribe({
      next: (value) => {
        if (value.isTradable !== value.wasTradable) {
          let accounts = Array.isArray(this.myAccountsByClientMap[value.clientId])
            ? this.myAccountsByClientMap[value.clientId]
            : Array.isArray(this.privateAccountsByClientMap[value.clientId])
              ? this.privateAccountsByClientMap[value.clientId]
              : [];

          for (let account of accounts) {
            this.mapItem(account);
          }
        }
      },
    });
  }

  getAccount(id: string, type?: ClientAccountVisibilityType | null): CubiqTS.AccountImpl {
    let account = this.getItem(id);

    if (
      account != null &&
      type != null &&
      type !== ClientAccountVisibilityType.getVisibilityType(account)
    ) {
      // if we are of an incorrect ClientAccountVisibilityType
      // fufil not found contract of undefined rather than null
      return undefined;
    }

    return account;
  }

  getActiveAccount(id: string, type?: ClientAccountVisibilityType | null): CubiqTS.AccountImpl {
    let account = this.getItem(id);
    if (
      (account != null &&
        type != null &&
        type !== ClientAccountVisibilityType.getVisibilityType(account)) ||
      (account != null && !account.ordersEnabledFg)
    ) {
      return undefined;
    } else {
      return account;
    }
  }

  getAccountsForClient(clientCd: string, onlyActive: boolean = false): Array<CubiqTS.AccountImpl> {
    if (!onlyActive) {
      return this.myAccountsByClientMap[clientCd];
    } else if (
      this.myAccountsByClientMap[clientCd] != null &&
      this.myAccountsByClientMap[clientCd].length > 0
    ) {
      let accounts: Array<CubiqTS.AccountImpl> = [];
      for (const account of this.myAccountsByClientMap[clientCd].values()) {
        if (account.ordersEnabledFg) {
          accounts.push(account);
        }
      }
      return accounts;
    } else {
      return null;
    }
  }

  getActiveBreakAndClientAccounts(
    clientCd: string,
    salesGroup: string,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];
    let clientAccounts = this.getAccountsForClient(clientCd, true);
    let breakAccounts = this.getBreakAccounts(salesGroup, true);
    if (
      clientAccounts != null &&
      clientAccounts.length > 0 &&
      breakAccounts != null &&
      breakAccounts.length > 0
    ) {
      accounts = clientAccounts.concat(
        breakAccounts.filter(function (item) {
          return clientAccounts.indexOf(item) < 0;
        }),
      );
    } else if (clientAccounts != null && clientAccounts.length > 0) {
      accounts = [].concat(clientAccounts);
    } else if (breakAccounts != null && breakAccounts.length > 0) {
      accounts = [].concat(breakAccounts);
    }
    return onlyFullVisibility ? this.filterFullyVisibleClients(accounts, 0) : accounts;
  }

  getClientsForAccount(accountId: string): Array<CubiqTS.ClientImpl> {
    let account = this.getAccount(accountId);
    let result = new Array<CubiqTS.ClientImpl>();
    if (account instanceof CubiqTS.AccountImpl && account.associatedClients != null) {
      account.associatedClients.forEach((clientId: string) => {
        let client = this.clientLookup.getItem(clientId);
        if (client instanceof CubiqTS.ClientImpl) {
          result.push(client);
        }
      });
      return result;
    } else {
      return null;
    }
  }

  /* same as lookupAccountById() but LESS efficient.
        Should be removed, kept to avoid breaking legacy code */
  lookupAccount(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    return this.lookupAccountsForId(codeToken, maxResults, onlyActive);
  }

  static isMatch(token: string, account: { id: string; name: string; bookingCode: string }) {
    let queryTokens =
      typeof token === "string"
        ? token
            .toLowerCase()
            .split(" ")
            .filter((token, i) => token != "" || i == 0)
        : [""];
    if (
      queryTokens.every(
        (token) =>
          (typeof account.id === "string" && account.id.toLowerCase().includes(token)) ||
          (typeof account.name === "string" && account.name.toLowerCase().includes(token)) ||
          (typeof account.bookingCode === "string" &&
            account.bookingCode.toLowerCase().includes(token)),
      )
    ) {
      return true;
    }
    return false;
  }

  lookupAccountsForMatch(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    benchmarkEnabled: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];

    for (const value of this.myItemMap.values()) {
      let account: CubiqTS.AccountImpl = value as CubiqTS.AccountImpl;

      if (benchmarkEnabled) {
        // need to test the branch of the client
        if (!this.hasBenchmarkEnabledClient(account)) {
          continue;
        }
      }

      if (!onlyActive || account.ordersEnabledFg) {
        if (AccountLookupService.isMatch(codeToken, account)) {
          accounts.push(account);
        }
      }
      if (maxResults > 0 && accounts.length >= maxResults) break;
    }

    return accounts;
  }

  lookupAccountsForId(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    benchmarkEnabled: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

    for (const value of this.myItemMap.values()) {
      let account: CubiqTS.AccountImpl = value as CubiqTS.AccountImpl;

      if (benchmarkEnabled) {
        // need to test the branch of the client
        if (!this.hasBenchmarkEnabledClient(account)) {
          continue;
        }
      }

      if (
        account.id.toLowerCase().indexOf(token) >= 0 &&
        (!onlyActive || account.ordersEnabledFg)
      ) {
        accounts.push(account);
      }
      if (maxResults > 0 && accounts.length >= maxResults) break;
    }
    return accounts;
  }

  hasBenchmarkEnabledClient(account: CubiqTS.AccountImpl): boolean {
    return account.associatedClients != null
      ? account.associatedClients.find((clientId: string) =>
          this.clientLookup.isBenchmarkEnabledClient(clientId),
        ) != null
      : false;
  }

  lookupAccountsForName(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    benchmarkEnabled: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

    // this.myItemMap.forEach((value, key) =>
    for (const value of this.myItemMap.values()) {
      let account: CubiqTS.AccountImpl = value as CubiqTS.AccountImpl;

      if (benchmarkEnabled) {
        // need to test the branch of the client
        if (!this.hasBenchmarkEnabledClient(account)) {
          continue;
        }
      }

      if (
        account.name.toLowerCase().indexOf(token) >= 0 &&
        (!onlyActive || account.ordersEnabledFg)
      ) {
        accounts.push(account);
      }
      if (maxResults > 0 && accounts.length >= maxResults) break;
    }
    return accounts;
  }

  lookupAccountsForBookingCode(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    benchmarkEnabled: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

    for (const value of this.myItemMap.values()) {
      let account: CubiqTS.AccountImpl = value as CubiqTS.AccountImpl;

      if (benchmarkEnabled) {
        // need to test the branch of the client
        if (!this.hasBenchmarkEnabledClient(account)) {
          continue;
        }
      }

      if (
        account.bookingCode.toLowerCase().indexOf(token) >= 0 &&
        (!onlyActive || account.ordersEnabledFg)
      ) {
        accounts.push(account);
      }
      if (maxResults > 0 && accounts.length >= maxResults) break;
    }
    return accounts;
  }

  lookupAccountsForContact(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    benchmarkEnabled: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

    for (const value of this.myItemMap.values()) {
      let account: CubiqTS.AccountImpl = value as CubiqTS.AccountImpl;
      if (benchmarkEnabled) {
        // need to test the branch of the client
        if (!this.hasBenchmarkEnabledClient(account)) {
          continue;
        }
      }
      if (
        account.contact.toLowerCase().indexOf(token) >= 0 &&
        (!onlyActive || account.ordersEnabledFg)
      ) {
        accounts.push(account);
      }
      if (maxResults > 0 && accounts.length >= maxResults) break;
    }
    return accounts;
  }

  lookupAccountsForClient(
    codeToken: string,
    clientCd: string,
    maxResults: number,
    onlyActive: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;
    let accounts: Array<CubiqTS.AccountImpl> = [];
    let accountsForClient = this.getAccountsForClient(clientCd);

    if (accountsForClient) {
      accountsForClient.forEach((account) => {
        if (
          account.id.toLowerCase().indexOf(token) >= 0 &&
          (maxResults <= 0 || accounts.length < maxResults) &&
          (!onlyActive || account.ordersEnabledFg)
        ) {
          accounts.push(account);
        }
      });
    }

    return accounts;
  }

  /**
   * Break account doesnt have clientId, but has salesGroupId
   * @param salesGroup
   * @param maxResults
   */
  getBreakAccounts(salesGroup: string, onlyActive: boolean = false): Array<CubiqTS.AccountImpl> {
    let accounts: Array<CubiqTS.AccountImpl> = [];

    for (const value of this.myItemMap.values()) {
      let account: CubiqTS.AccountImpl = value as CubiqTS.AccountImpl;
      if (
        (account.clientId == null || account.clientId === "") &&
        (account.salesGroupId as CubiqTS.Long).toString() === salesGroup &&
        (!onlyActive || account.ordersEnabledFg)
      ) {
        accounts.push(account);
      }
    }
    return accounts;
  }

  lookupActiveAccounts(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Array<CubiqTS.AccountImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupAccountsForMatch(searchToken, 0, true),
          maxResults,
        )
      : this.lookupAccountsForMatch(searchToken, maxResults, true);
  }

  lookupActiveAccountsForId(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Array<CubiqTS.AccountImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(this.lookupAccountsForId(searchToken, 0, true), maxResults)
      : this.lookupAccountsForId(searchToken, maxResults, true);
  }

  lookupActiveAccountsForName(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Array<CubiqTS.AccountImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(this.lookupAccountsForName(searchToken, 0, true), maxResults)
      : this.lookupAccountsForName(searchToken, maxResults, true);
  }

  lookupActiveBMAccounts(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Array<CubiqTS.AccountImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupAccountsForMatch(searchToken, 0, true, true),
          maxResults,
        )
      : this.lookupAccountsForMatch(searchToken, maxResults, true, true);
  }

  lookupActiveBMAccountsForId(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Array<CubiqTS.AccountImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupAccountsForId(searchToken, 0, true, true),
          maxResults,
        )
      : this.lookupAccountsForId(searchToken, maxResults, true, true);
  }

  lookupBMarkAccountsForName(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Array<CubiqTS.AccountImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupAccountsForName(searchToken, 0, true, true),
          maxResults,
        )
      : this.lookupAccountsForName(searchToken, maxResults, true, true);
  }

  /// OMS-31021
  // Filters the dataset of accounts, based on users visibility, only fully visible accounts are returend
  private filterFullyVisibleClients(
    dataset: Array<CubiqTS.AccountImpl>,
    maxResults: number,
  ): Array<CubiqTS.AccountImpl> {
    if (Array.isArray(dataset) && dataset.length > 0) {
      let result: Array<CubiqTS.AccountImpl> = new Array<CubiqTS.AccountImpl>();
      for (let account of dataset) {
        if (
          account instanceof CubiqTS.AccountImpl &&
          this.clientAccountVisibilityService._getVisibilityType(account) ===
            ClientAccountVisibilityType.Full
        ) {
          result.push(account);
        }

        if (maxResults > 0 && result.length >= maxResults) {
          break;
        }
      }
      return result;
    } else {
      return [];
    }
  }
}
